'use client';

import { useState, useEffect } from 'react';
import { FiUsers, FiMapPin, FiCalendar, FiPhone, FiEye } from 'react-icons/fi';

interface ContactView {
  contact_application_id: number;
  viewed_at: string;
  name: string;
  mobile: string;
  teacher_category: string;
  class: string;
  subject: string;
  current_school_name: string;
  district_name: string;
  block_name: string;
}

interface MapView {
  school_udise: string;
  school_name: string;
  district: string;
  block: string;
  viewed_at: string;
}

interface ViewHistoryProps {
  className?: string;
}

export default function ViewHistory({ className = '' }: ViewHistoryProps) {
  const [contactViews, setContactViews] = useState<ContactView[]>([]);
  const [mapViews, setMapViews] = useState<MapView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'contacts' | 'maps'>('contacts');

  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- stable local function invoked once for its async side effect
    fetchViewHistory();
  }, []);

  const fetchViewHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/user/view-history', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch view history');
      }

      const data = await response.json();

      if (data.success) {
        setContactViews(data.data.contactViews);
        setMapViews(data.data.mapViews);
      } else {
        throw new Error(data.error || 'Failed to load view history');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load view history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleShareWhatsApp = (mobile: string) => {
    const message = `Hi! I found you through mutual transfer matching. I'm interested in discussing a potential transfer. Please contact me if interested.`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/91${mobile}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <div className={`bg-white border border-slate-200 rounded-lg p-6 shadow-sm ${className}`}>
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
        <p className="text-slate-600 text-sm text-center">Loading view history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-slate-200 rounded-lg shadow-sm ${className}`}>
      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        <button
          onClick={() => setActiveTab('contacts')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'contacts'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <FiUsers size={16} />
          Contact Views ({contactViews.length})
        </button>
        <button
          onClick={() => setActiveTab('maps')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'maps'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <FiMapPin size={16} />
          Map Views ({mapViews.length})
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {activeTab === 'contacts' ? (
          <div className="space-y-3">
            {contactViews.length === 0 ? (
              <div className="text-center py-8">
                <FiUsers className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">No contact views yet</p>
              </div>
            ) : (
              contactViews.map((view) => (
                <div key={view.contact_application_id} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-slate-900">{view.name}</h4>
                      <p className="text-sm text-slate-600">
                        {view.teacher_category} • {view.class} • {view.subject}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleShareWhatsApp(view.mobile)}
                        className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                      >
                        <FiPhone size={12} />
                        WhatsApp
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-500">School:</span>
                      <p className="text-slate-900">{view.current_school_name}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Location:</span>
                      <p className="text-slate-900">{view.district_name}, {view.block_name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">
                    <FiCalendar size={12} />
                    <span>Viewed: {formatDate(view.viewed_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {mapViews.length === 0 ? (
              <div className="text-center py-8">
                <FiMapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">No map views yet</p>
              </div>
            ) : (
              mapViews.map((view, index) => (
                <div key={index} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-slate-900">
                        {view.school_udise === 'PAGE_ACCESS' ? 'Map Page Access' : (view.school_name || 'School Location')}
                      </h4>
                      <p className="text-sm text-slate-600">
                        {view.district}, {view.block}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 text-green-600">
                      <FiEye size={14} />
                      <span className="text-xs">Viewed</span>
                    </div>
                  </div>
                  
                  {view.school_udise && view.school_udise !== 'PAGE_ACCESS' && (
                    <div className="text-sm">
                      <span className="text-slate-500">UDISE:</span>
                      <span className="text-slate-900 ml-1">{view.school_udise}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">
                    <FiCalendar size={12} />
                    <span>Viewed: {formatDate(view.viewed_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
